import React, { useContext, useMemo } from 'react';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { EventConditions } from '../BuilderFields';
import { IRowMeta } from '../../../redux/slices/oldBuilder';
import {
  eventConditionsPrimitiveSelector,
  negativeStepRowSelector,
} from '../../../redux/selectors/builder';
import {
  HighlightedEntity,
  useHighlightingContext,
  withHighlightingContext,
} from '../../HighlightingContext';
import { isEmptyEventConditions } from '../../../utils/builder';
import { useAppSelector } from '../../../redux/hooks';
import BuilderStepHeader from './BuilderStepHeader';
import { StepEventMenuOpenContext } from '../../../utils/contexts/StepEventMenuOpenContext';
import * as BuilderUI from '../BuilderUI';
import ExcludedEventConditions from './ExcludedEventConditions';
import {
  useActionsVisibilityContext,
  withActionsVisibilityContext,
} from '../../ActionsVisibilityContext';
import { IEventConditionFieldsFragment } from '../../../utils/apollo/graphqlSchema';
import DndDroppableZone from '../../DndEntity/DndDroppableZone';
import { EntityDropzoneType } from '../../QuickAccessDictionary/DictionaryTabs/types';
import {
  createDictionaryEntityDropzoneId,
  getDndActiveItemData,
} from '../../DndEntity/utils';
import {
  EntityDropzoneDataTypeEnum,
  IEventEntityDropzoneDataType,
} from '../../DndEntity/validation';
import { MAX_EVENT_CONDITIONS_COUNT } from '../../../config/constants';
import { useDndEntityInternalContext } from '../../DndEntity';
import { borderedContent } from '../../../styles/shareableStyles';
import { theme } from '../../../styles/theme';

interface IProps {
  rowMeta: IRowMeta;
  dragHandleProps: DraggableProvidedDragHandleProps | null | undefined;
}

const Container = styled(HighlightedEntity)<{ $isInactive?: boolean }>`
  opacity: ${({ $isInactive }) => ($isInactive ? 0.5 : 1)};
  ${borderedContent};
`;

const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  padding: 8px 16px;
  border-top: 1px solid ${theme.colors.border};
`;

const BuilderStep = ({ rowMeta, dragHandleProps }: IProps) => {
  const { rowIndex } = rowMeta;

  const { activeItem } = useDndEntityInternalContext();

  const eventConditionsSelector = useMemo(eventConditionsPrimitiveSelector, []);
  const eventConditions = useAppSelector((state) =>
    eventConditionsSelector(state, rowMeta),
  );
  const excludedEventConditions: IEventConditionFieldsFragment[] =
    useAppSelector((state) => negativeStepRowSelector(state, rowMeta));

  const { showActions, hideActions } = useActionsVisibilityContext();
  const { activateHighlighting, deactivateHighlighting } =
    useHighlightingContext();
  const { isStepEventMenuOpen } = useContext(StepEventMenuOpenContext);

  // the first 2 steps are always active.
  const isStepEmpty = isEmptyEventConditions(eventConditions) && rowIndex > 1;

  const newEventsDropzoneId = createDictionaryEntityDropzoneId(
    EntityDropzoneType.Events,
    rowMeta,
  );

  const areEventConditionsEmpty = isEmptyEventConditions(eventConditions);

  const checkHasErrors = () => {
    const data = getDndActiveItemData(activeItem);

    const isTagLeafEvent =
      data?.__typename === 'TagLeafBasicEvent' ||
      data?.__typename === 'TagLeafVirtualEvent';

    return (
      isTagLeafEvent && eventConditions.length >= MAX_EVENT_CONDITIONS_COUNT
    );
  };

  const getCustomMessage = () => {
    if (eventConditions.length >= MAX_EVENT_CONDITIONS_COUNT) {
      return `Limit of 5 Events reached`;
    }

    return `+ Add Event to Step ${rowMeta.rowIndex + 1}`;
  };

  return (
    <DndDroppableZone
      accepts={[EntityDropzoneType.Events]}
      id={newEventsDropzoneId}
      data={
        {
          id: newEventsDropzoneId,
          type: EntityDropzoneDataTypeEnum.Events,
          rowMeta: {
            ...rowMeta,
            eventIndex: areEventConditionsEmpty ? 0 : eventConditions.length,
          },
          isNewEvent: !areEventConditionsEmpty,
          shouldAddMeasureFunction: areEventConditionsEmpty,
        } satisfies IEventEntityDropzoneDataType
      }
      customMessage={getCustomMessage()}
      hasError={checkHasErrors()}
    >
      <Container
        onMouseEnter={showActions}
        onMouseLeave={hideActions}
        $isInactive={!isStepEventMenuOpen && isStepEmpty}
      >
        <BuilderStepHeader
          rowMeta={rowMeta}
          dragHandleProps={dragHandleProps}
          onRemoveMouseEnter={activateHighlighting}
          onRemoveMouseLeave={deactivateHighlighting}
        />
        <ContentWrapper>
          {excludedEventConditions?.length > 0 && (
            <>
              <ExcludedEventConditions
                rowMeta={rowMeta}
                eventConditions={excludedEventConditions}
              />
              <BuilderUI.HorizontalDivider />
            </>
          )}
          <EventConditions rowMeta={rowMeta} label={'Event(s)'} />
        </ContentWrapper>
      </Container>
    </DndDroppableZone>
  );
};

export default withHighlightingContext(
  withActionsVisibilityContext(BuilderStep),
);
